import { Survey } from "@/types";
import { useApiData } from "@/contexts/ApiData";
import { useDateFormatter, useTranslations } from "@/contexts/Translation";
import { H2 } from "../Headers/Headings";
import Section from "../Section";
import SurveyShortView from "./ShortView";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import SystemMessage from "../SystemMessage/Index";
import { InfoMessage } from "../InfoMessage/InfoMessage";
import { useProfile } from "@/contexts/UserProvider";
import CreateLink from "../CreateLink/CreateLink";

type Translate = (key: string, ...args: (string | number | undefined)[]) => string;

export const surveyShortTitle = ({ surveyName, status, endDate }: Pick<Survey, "surveyName" | "status" | "endDate">, t: Translate, d: (date: string) => string) => {
  if (surveyName) {
    return t(status === "finished" ? "titleNameFinished" : "titleNameOngoing", surveyName, d(endDate));
  }
  return t(status === "finished" ? "titleFinished" : "titleOngoing", d(endDate));
};

type SurveysProps = {
  limitFinished?: number;
  isShared?: boolean;
};

const Surveys = ({ isShared = false, limitFinished = -1 }: SurveysProps) => {
  const t = useTranslations("surveys");
  const d = useDateFormatter();
  const { get } = useApiData();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const profile = useProfile();

  useEffect(() => {
    const callOwnSurveys = async () => {
      const surveyList = await get("/survey");
      setSurveys(surveyList);
      setIsLoading(false);
    };
    const callSharedSurveys = async () => {
      const surveys = await get(`/shared/surveys`);
      setSurveys(surveys);
      setIsLoading(false);
    };
    if (isShared) {
      callSharedSurveys();
    } else {
      callOwnSurveys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSurveyShortTitle = (s: Survey) => surveyShortTitle(s, t, d);

  const listSurveys = (list: Survey[]) =>
    list.map((s) => {
      return <SurveyShortView isShared={isShared} key={s.id} title={getSurveyShortTitle(s)} {...s} />;
    });

  const ongoingSurveys = surveys.filter((s: Survey) => s.status === "ongoing");
  const finishedSurveys = surveys
    .filter((s: Survey) => s.status !== "ongoing")
    .filter((s, i) => {
      if (limitFinished > 0 && limitFinished <= i) {
        return false;
      }
      return s.status === "finished";
    });

  if (isLoading) {
    return <SystemMessage message='loading' />;
  }

  const createSurvey = () => {
    navigate(`/survey`);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <Section span={8}>
      <H2>{t(isShared ? "sharedHeading" : "title")}</H2>
      {surveys.length === 0 && <InfoMessage>{t(isShared ? "noSharedSurveys" : "noSurveys")}</InfoMessage>}
      {listSurveys(ongoingSurveys)}
      {!isShared &&
        (!profile?.hasValidSubscription && surveys.length > 0 ? (
          <InfoMessage>{t("onlyOneSurvey")}</InfoMessage>
        ) : (
          <CreateLink createFn={createSurvey} text={t("createSurvey")} />
        ))}
      {listSurveys(finishedSurveys)}
    </Section>
  );
};

export default Surveys;
